/**
 * Экраны шагов анкеты-онбординга. Каждый шаг получает:
 *   a        — текущие ответы
 *   patch    — изменить ответы
 *   choose   — изменить ответы и сразу перейти дальше (для карточек)
 *   next     — «Далее» с проверкой (Enter в поле)
 *   error    — ошибка текущего шага
 * Заголовки, подписи и кнопка шага — в STEP_VIEWS внизу файла.
 */
import { useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
    ChoiceGroup, Chip, Field, RangeCard, Checkbox, Notice, PasswordToggle,
    IconPin, IconSearch, IconMail, IconPhone, IconLock, IconPlus, IconMinus,
    IconCamera, IconCheck,
} from '../Auth/ui';
import {
    GENDERS, COUPLE_TYPES, INTERESTS, PURPOSES, INCOMES, POPULAR_CITIES, ABOUT_PROMPTS,
    AGE_MIN, AGE_MAX, ABOUT_MAX, genderPatch, interestPatch, formatPhone, phoneDigits,
} from './onboardingConfig';


// ============================== ВЫБОР КАРТОЧКОЙ ==============================
function GenderStep({ a, choose }) {
    return (
        <ChoiceGroup options={GENDERS} value={a.genderChoice}
                     onChange={(v) => choose(v === a.genderChoice ? {} : genderPatch(v))} />
    );
}

function CoupleStep({ a, choose }) {
    return <ChoiceGroup layout="list" options={COUPLE_TYPES} value={a.gender} onChange={(v) => choose({ gender: v })} />;
}

function InterestStep({ a, choose }) {
    return <ChoiceGroup layout="list" options={INTERESTS} value={a.interest} onChange={(v) => choose(interestPatch(v, a))} />;
}

function PurposeStep({ a, choose }) {
    return <ChoiceGroup options={PURPOSES} value={a.purpose} onChange={(v) => choose({ purpose: v })} />;
}

function IncomeStep({ a, choose }) {
    return <ChoiceGroup layout="list" options={INCOMES} value={a.income} onChange={(v) => choose({ income: v })} />;
}


// ============================== ТЕКСТ И ЧИСЛА ==============================
function NameStep({ a, patch, next, error }) {
    return (
        <Field size="xl" autoFocus placeholder="Ваше имя" value={a.name} maxLength={40}
               autoComplete="given-name" enterKeyHint="next" error={error}
               onChange={(e) => patch({ name: e.target.value.replace(/^\s+/, '') })}
               onKeyDown={(e) => { if (e.key === 'Enter') next(); }} />
    );
}

function AgeStep({ a, patch, error }) {
    const set = (v) => patch({ age: Math.max(AGE_MIN, Math.min(AGE_MAX, v)) });
    const p = ((a.age - AGE_MIN) / (AGE_MAX - AGE_MIN)) * 100;
    return (
        <>
            <div className="lv-age">
                <button type="button" className="lv-round" aria-label="Меньше"
                        disabled={a.age <= AGE_MIN} onClick={() => set(a.age - 1)}>
                    <IconMinus width={26} height={26} />
                </button>
                <motion.div key={a.age} className="lv-age__num" aria-live="polite"
                            initial={{ scale: 0.86, opacity: 0.5 }} animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 26 }}>
                    {a.age}
                </motion.div>
                <button type="button" className="lv-round" aria-label="Больше"
                        disabled={a.age >= AGE_MAX} onClick={() => set(a.age + 1)}>
                    <IconPlus width={26} height={26} />
                </button>
            </div>
            <input type="range" className="lv-range" min={AGE_MIN} max={AGE_MAX} value={a.age}
                   style={{ '--p': `${p}%` }} aria-label="Возраст"
                   onChange={(e) => set(Number(e.target.value))} />
            {error ? <Notice tone="error">{error}</Notice> : null}
            <p className="lv-hint">Lovirds — только для совершеннолетних, 18+</p>
        </>
    );
}

function CityStep({ a, patch, choose, next, error }) {
    const current = a.city.trim().toLowerCase();
    return (
        <>
            <Field icon={<IconPin />} placeholder="Например, Москва" value={a.city} maxLength={120}
                   autoComplete="address-level2" enterKeyHint="next" error={error}
                   onChange={(e) => patch({ city: e.target.value })}
                   onKeyDown={(e) => { if (e.key === 'Enter') next(); }} />
            <div className="lv-chips">
                {POPULAR_CITIES.map((c) => (
                    <Chip key={c} selected={current === c.toLowerCase()} onClick={() => choose({ city: c })}>{c}</Chip>
                ))}
            </div>
        </>
    );
}

function BodyStep({ a, patch }) {
    return (
        <>
            <RangeCard label="Рост" unit="см" min={140} max={220} value={a.height} empty={!a.heightTouched}
                       onChange={(v) => patch({ height: v, heightTouched: true })} />
            <RangeCard label="Вес" unit="кг" min={40} max={160} value={a.weight} empty={!a.weightTouched}
                       onChange={(v) => patch({ weight: v, weightTouched: true })} />
            <p className="lv-hint">Можно пропустить и заполнить позже в профиле</p>
        </>
    );
}


// ============================== СПРАВОЧНИКИ ==============================
function FieldStep({ a, patch, lists, error }) {
    const all = lists.fields;
    const [q, setQ] = useState(a.field && !a.field.id ? a.field.name : '');
    const query = q.trim().toLowerCase();
    const shown = useMemo(
        () => (query ? all.filter((f) => f.name.toLowerCase().includes(query)) : all).slice(0, 24),
        [all, query],
    );
    const exact = all.find((f) => f.name.toLowerCase() === query);

    const onType = (value) => {
        setQ(value);
        const v = value.trim();
        const match = all.find((f) => f.name.toLowerCase() === v.toLowerCase());
        patch({ field: v ? (match ? { id: match.id, name: match.name } : { id: null, name: v }) : null });
    };

    return (
        <>
            <Field icon={<IconSearch />} placeholder="Найдите или впишите свою" value={q} maxLength={120}
                   error={error} onChange={(e) => onType(e.target.value)} />
            <div className="lv-chips">
                {query && !exact ? (
                    <Chip className="lv-chip--add" selected={!a.field?.id && !!a.field}
                          onClick={() => patch({ field: { id: null, name: q.trim() } })}>
                        <IconPlus width={16} height={16} /> {q.trim()}
                    </Chip>
                ) : null}
                {shown.map((f) => (
                    <Chip key={f.id} selected={a.field?.id === f.id}
                          onClick={() => { setQ(''); patch({ field: { id: f.id, name: f.name } }); }}>
                        {f.name}
                    </Chip>
                ))}
            </div>
        </>
    );
}

/** Мультивыбор увлечений. Используется для своих и для увлечений партнёров. */
function HobbiesPicker({ a, patch, lists, idsKey, customKey }) {
    const all = lists.hobbies;
    const ids = a[idsKey];
    const custom = a[customKey];
    const [q, setQ] = useState('');
    const query = q.trim().toLowerCase();

    const toggle = (id) => patch({ [idsKey]: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] });
    const removeCustom = (name) => patch({ [customKey]: custom.filter((x) => x !== name) });
    const add = () => {
        const v = q.trim();
        if (!v) return;
        const match = all.find((h) => h.name.toLowerCase() === v.toLowerCase());
        if (match) { if (!ids.includes(match.id)) toggle(match.id); }
        else if (!custom.some((c) => c.toLowerCase() === v.toLowerCase())) patch({ [customKey]: [...custom, v] });
        setQ('');
    };
    const shown = query ? all.filter((h) => h.name.toLowerCase().includes(query)) : all;

    return (
        <>
            <Field icon={<IconSearch />} placeholder="Найти или добавить своё" value={q} maxLength={60}
                   enterKeyHint="done"
                   onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                   end={q.trim() ? (
                       <button type="button" className="lv-field__end" onClick={add} aria-label="Добавить">
                           <IconPlus />
                       </button>
                   ) : null} />
            <div className="lv-chips">
                {custom.map((name) => (
                    <Chip key={`c-${name}`} selected onClick={() => removeCustom(name)}>{name}</Chip>
                ))}
                {shown.map((h) => (
                    <Chip key={h.id} selected={ids.includes(h.id)} onClick={() => toggle(h.id)}>{h.name}</Chip>
                ))}
            </div>
        </>
    );
}

function HobbiesStep(props) {
    return <HobbiesPicker {...props} idsKey="hobbies" customKey="customHobbies" />;
}

function PartnerHobbiesStep(props) {
    return <HobbiesPicker {...props} idsKey="partnerHobbies" customKey="partnerCustomHobbies" />;
}

function AboutStep({ a, patch }) {
    const addPrompt = (chipText) => {
        const text = chipText.replace(/^\S+\s/, '');        // без эмодзи
        const cur = a.about.trim();
        const joined = cur ? `${cur}${/[.!?…]$/.test(cur) ? '' : '.'} ${text}` : text;
        patch({ about: joined.slice(0, ABOUT_MAX) });
    };
    return (
        <>
            <textarea className="lv-textarea" value={a.about} maxLength={ABOUT_MAX} rows={5}
                      placeholder="Например: люблю горы и джаз, ищу человека, с которым легко и интересно"
                      onChange={(e) => patch({ about: e.target.value })} />
            <div className="lv-textarea__meta">{a.about.length}/{ABOUT_MAX}</div>
            <div className="lv-chips">
                {ABOUT_PROMPTS.map((p) => <Chip key={p} onClick={() => addPrompt(p)}>{p}</Chip>)}
            </div>
        </>
    );
}


// ============================== ФОТО ==============================
function PhotoStep({ a, photo, photoBusy, onPickPhoto, error }) {
    const inputRef = useRef(null);
    const open = () => inputRef.current?.click();
    const couple = a.genderChoice === 'couple';
    return (
        <>
            <motion.div className={`lv-photo${photo ? ' has-photo' : ''}`} role="button" tabIndex={0}
                        aria-label={photo ? 'Заменить фото' : 'Загрузить фото'}
                        onClick={open}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
                        initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
                        whileTap={{ scale: 0.97 }}>
                {photo ? (
                    <>
                        <img src={photo.url} alt="Ваше фото" />
                        <span className="lv-photo__replace">Заменить фото</span>
                    </>
                ) : (
                    <div className="lv-photo__empty">
                        <span className="lv-photo__icon" aria-hidden="true"><IconCamera width={44} height={44} /></span>
                        <span className="lv-photo__cta">{photoBusy ? 'Обрабатываем…' : 'Загрузить фото'}</span>
                        <span className="lv-photo__sub">JPG или PNG</span>
                    </div>
                )}
            </motion.div>
            <input ref={inputRef} type="file" accept="image/*" hidden
                   onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onPickPhoto(f); }} />
            {error ? <Notice tone="error">{error}</Notice> : null}
            <div className="lv-tips">
                <span><IconCheck width={14} height={14} strokeWidth={3} /> Лицо хорошо видно</span>
                <span>
                    <IconCheck width={14} height={14} strokeWidth={3} />{' '}
                    {couple ? 'Видно вас обоих' : 'Вы на фото один'}
                </span>
                <span><IconCheck width={14} height={14} strokeWidth={3} /> Без сильных фильтров</span>
            </div>
        </>
    );
}


// ============================== АККАУНТ ==============================
function AccountStep({ a, patch, errors }) {
    const [showPw, setShowPw] = useState(false);
    return (
        <>
            <Field label="Email" icon={<IconMail />} type="email" inputMode="email" autoComplete="email"
                   placeholder="you@mail.ru" value={a.email} error={errors.email}
                   onChange={(e) => patch({ email: e.target.value.trim() })} />
            <Field label="Телефон" icon={<IconPhone />} type="tel" inputMode="tel" autoComplete="tel"
                   placeholder="+7 (900) 000-00-00" value={formatPhone(a.phone)} error={errors.phone}
                   onChange={(e) => patch({ phone: phoneDigits(e.target.value) })} />
            <Field label="Пароль" icon={<IconLock />} type={showPw ? 'text' : 'password'} autoComplete="new-password"
                   placeholder="Минимум 8 символов, буквы и цифры" value={a.password} error={errors.password}
                   onChange={(e) => patch({ password: e.target.value })}
                   end={<PasswordToggle shown={showPw} onToggle={() => setShowPw((s) => !s)} />} />
            <Checkbox checked={a.consent} onChange={(v) => patch({ consent: v })}>
                Мне есть 18 лет. Принимаю пользовательское соглашение и даю согласие на обработку персональных данных
            </Checkbox>
            {errors.form ? <Notice tone="error">{errors.form}</Notice> : null}
        </>
    );
}


// ============================== ТАБЛИЦА ШАГОВ ==============================
const countLabel = (n) => (n ? `Далее · ${n}` : 'Пропустить');

export const STEP_VIEWS = {
    gender: {
        title: 'Кто вы?', subtitle: 'Заполните анкету — это займёт пару минут', editTitle: 'Пол',
        auto: true, hero: true, Content: GenderStep,
    },
    coupleType: { title: 'Ваша пара', subtitle: 'Кто в паре?', editTitle: 'Ваша пара', auto: true, Content: CoupleStep },
    interest: { title: 'Кого вы ищете?', subtitle: 'Покажем подходящие анкеты', editTitle: 'Кого ищете', auto: true, Content: InterestStep },
    purpose: {
        title: 'Цель знакомства', subtitle: 'Это поможет подобрать людей с теми же ожиданиями', editTitle: 'Цель знакомства',
        auto: true, Content: PurposeStep,
    },
    name: { title: 'Как вас зовут?', subtitle: 'Имя увидят в вашей анкете', editTitle: 'Имя', Content: NameStep },
    age: { title: 'Сколько вам лет?', subtitle: 'Возраст тоже будет в анкете', editTitle: 'Возраст', Content: AgeStep },
    city: { title: 'Где вы живёте?', subtitle: 'Чтобы показывать людей рядом', editTitle: 'Город', Content: CityStep },
    body: {
        title: 'Рост и вес', subtitle: 'Необязательно, но помогает в подборе', editTitle: 'Рост и вес', Content: BodyStep,
        cta: (a) => (a.heightTouched || a.weightTouched ? 'Далее' : 'Пропустить'),
    },
    income: { title: 'Ваш доход', subtitle: 'Помогает точнее подобрать пару', editTitle: 'Доход', auto: true, Content: IncomeStep },
    field: { title: 'Чем вы занимаетесь?', subtitle: 'Выберите сферу или впишите свою', editTitle: 'Сфера деятельности', Content: FieldStep },
    hobbies: {
        title: 'Ваши увлечения', subtitle: 'Общие интересы — лучший повод написать первым', editTitle: 'Увлечения',
        Content: HobbiesStep, cta: (a) => countLabel(a.hobbies.length + a.customHobbies.length),
    },
    partnerHobbies: {
        title: 'Увлечения пары', subtitle: 'Что должно нравиться тем, кого вы ищете?', editTitle: 'Увлечения пары',
        Content: PartnerHobbiesStep, cta: (a) => countLabel(a.partnerHobbies.length + a.partnerCustomHobbies.length),
    },
    about: {
        title: 'Пару слов о себе', subtitle: 'Что важно знать тому, кто вам напишет?', editTitle: 'О себе',
        Content: AboutStep, cta: (a) => (a.about.trim() ? 'Далее' : 'Пропустить'),
    },
    photo: { title: 'Добавьте фото', subtitle: 'Фото — первое, что увидят в вашей анкете', editTitle: 'Фото', Content: PhotoStep },
    account: {
        title: 'Последний шаг', subtitle: 'Сохраните анкету — и можно знакомиться',
        Content: AccountStep, cta: () => 'Создать анкету',
    },
};
